/**
 * THE TERMS ACCEPTANCE / RE-ACCEPTANCE INTERSTITIAL.  Route `/terms`.
 *
 * ONE SCREEN, A BOX PER DOCUMENT (design 25; design 24, T24's third place). It lists EVERY
 * SUBMISSION DOCUMENT this author owes - the author agreement and the editorial standards, each
 * with its version, its link and one box, never pre-ticked - and "Accept and continue" records
 * them in one `acceptAgreements` call. The corpus fixed the shape at one screen ("on one
 * interstitial screen with one click", `docs/design/04-schema.md`); design 25 keeps the
 * one screen and gives each separately recorded consent its own box. A payout document is not
 * here: it is accepted on Payment details, where what it unlocks is a payout.
 *
 * NOBODY IS EVER CREDITED WITH AN ACCEPTANCE THEY DID NOT MAKE (`WC-IK-07`, decision-log row 92).
 * The ~530 are ASKED, never back-filled — "the old signup checkbox pointed at nothing, so there is
 * no document any of them can be said to have accepted". For them this is a FIRST acceptance
 * recorded through the `cabinet_reaccept` channel, because `'application'` cannot apply to authors
 * who never applied. The client does not know or send that; see below.
 *
 * THE REQUEST CARRIES DOCUMENTS AND VERSIONS AND NOTHING ELSE: `{ accept: [{ documentCode,
 * versionLabel }] }` (design 25, 3.1), or `{ versionLabel }` on `acceptTerms`. The acceptance
 * CHANNEL — `terms_acceptance_channel`, a two-value Postgres enum of `'application'` and
 * `'cabinet_reaccept'` (`04-schema.md`) — is written by the SERVER from which caller it is. A
 * client cannot influence it and must not try.
 *
 * WHEN THE LIST WILL NOT LOAD, the author agreement's own read answers for it and its acceptance
 * goes through `acceptTerms`, as it always did (./agreements.ts) - the one document every server
 * has, so this screen never becomes a wall because a list failed.
 *
 * THE DOCUMENT IS NOT OURS TO RENDER. G28 rules it is rendered by moonx.app; the platform stores
 * the URL and a digest of the bytes, never the text. So this screen LINKS to `contentUrl` and never
 * embeds, proxies or iframes it.
 *
 * THERE IS NO WITHDRAWAL CONTROL, EVER. "There is no withdrawal path and no `revoked_at`. Accepting
 * a contract is not consent" (`04-schema.md`). Withdrawable consents live in
 * `author_consent_event` and are a different surface. Do not draw one here.
 *
 * HOW PEOPLE ARRIVE, AND WHY THREE OPTIONAL CALLBACKS. From the support page's own button, and - in
 * a live build only - from the session gate, which puts this screen in front of the first cabinet
 * screen an author with a stale acceptance opens, once per visit (`termsInterstitialDue`,
 * features/auth/session/session-state.ts). The live route (`routes/support/session-routes.tsx`)
 * then passes `onAccepted` (re-read `GET /v1/me`, so the session stops saying it is owed),
 * `onNotNow` - back to the address's `next`, or to the support page when it carries none - and,
 * with a `next`, `onContinue` to that same screen. The default build passes neither: no "Not now",
 * and the way on is the support page, exactly as it always was. "Accept and continue" takes it at
 * once; "Continue" is offered when nothing was owed.
 *
 * MONEY. The documents on this screen hold back sending an article for review, and never a payout
 * run crediting the author: the screen says so, because an author asked to sign something before
 * they can work will assume the worst about their money unless told plainly. (Since design 25 the
 * payout documents and complete Payment details can hold a payout, from a date - they are not here.)
 */
import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Container,
  Group,
  Stack,
  Text,
  Title,
  VisuallyHidden,
} from '@mantine/core';
import { Trans, useTranslation } from '../../i18n';
import { tooManyRequests } from '../cabinet/api/problem';
import type { Agreement } from './api/contract';
import { acceptAgreements, acceptTerms } from './api/operations';
import { ThrottledError, isRetryable, isUnauthenticated, toApiError } from './api/errors';
import { IS_FIXTURE_TRANSPORT, useApiTransport } from './api/transport';
import type { ApiTransport } from './api/transport';
import { acceptWords, agreementName, owedSubmissionDocuments } from './agreements';
import { FailureBlock, LoadingBlock } from './components/StateBlocks';
import { OffsiteMark } from './components/OffsiteMark';
import { useAgreementsToAccept } from './hooks/use-support-data';
import type { AgreementsToAccept } from './hooks/use-support-data';
import { formatDay } from './format';
import { CABINET_ROUTES, useNavigate } from './navigation';
import classes from './support.module.css';

/** Where the accept POST got to. The READ's state lives in the resource, not here. */
type AcceptPhase =
  | 'idle'
  | 'submitting'
  /** Recorded - or `acceptTerms`' 409, which is the same outcome seen from a second tab. */
  | 'accepted'
  /** 404: a newer version was published while this screen sat open. */
  | 'superseded'
  /** 429. */
  | 'throttled'
  /** 400, a 5xx, or a request that never arrived. */
  | 'failed';

export interface TermsInterstitialProps {
  /**
   * True when the author reached this screen because sending an article for review was refused.
   * Say so — an interstitial that appears with no explanation reads as a demand.
   */
  readonly blockedAction?: boolean;
  /** Called once the acceptance is recorded. */
  readonly onAccepted?: (() => void) | undefined;
  /** Where the screen goes on. Absent, the support page - where it always went. */
  readonly onContinue?: (() => void) | undefined;
  /**
   * "Not now". Present in a live build, and it records nothing: it is the way back - to the screen
   * the gate detoured from, or to the support page - which is what keeps an interstitial from being
   * a wall (docs/design/research/26-authors-identity-application-research.md).
   */
  readonly onNotNow?: (() => void) | undefined;
}

/** A document's one box: an id the focus can move to. */
function boxId(document: Agreement): string {
  return `terms-accept-${document.documentCode}`;
}

/**
 * Records the owed documents - in one `acceptAgreements` call, or, where the list was the author
 * agreement's own read, through `acceptTerms` - and says whether it landed.
 */
async function record(
  transport: ApiTransport,
  source: AgreementsToAccept,
  owed: readonly Agreement[],
): Promise<void> {
  if (source.via === 'terms') {
    const label = owed[0]?.live?.versionLabel;
    if (label !== undefined) {
      await acceptTerms(transport, { versionLabel: label });
    }
    return;
  }
  await acceptAgreements(transport, {
    accept: owed.flatMap((document) =>
      document.live === null
        ? []
        : [{ documentCode: document.documentCode, versionLabel: document.live.versionLabel }],
    ),
  });
}

export function TermsInterstitial({
  blockedAction = false,
  onAccepted,
  onContinue,
  onNotNow,
}: TermsInterstitialProps) {
  const { t } = useTranslation('support');
  const transport = useApiTransport();
  const navigate = useNavigate();
  const agreements = useAgreementsToAccept();
  const [phase, setPhase] = useState<AcceptPhase>('idle');
  const [ticked, setTicked] = useState<Readonly<Record<string, boolean>>>({});
  const [unticked, setUnticked] = useState(false);
  // The wait a 429 named, when it named one. Said, never counted down: pressing again is the retry.
  const [throttleWait, setThrottleWait] = useState<number | undefined>(undefined);

  const source = agreements.data;
  const owed = source === null ? [] : owedSubmissionDocuments(source.list);
  const reload = agreements.reload;

  const goBack = useCallback(() => {
    if (onContinue !== undefined) {
      onContinue();
      return;
    }
    navigate(CABINET_ROUTES.support);
  }, [onContinue, navigate]);

  const onAccept = useCallback(() => {
    if (source === null) {
      return;
    }
    // One box per document, every one ticked: a box left empty is said, and focus goes to it.
    const empty = owed.find((document) => ticked[boxId(document)] !== true);
    if (empty !== undefined) {
      setUnticked(true);
      window.document.getElementById(boxId(empty))?.focus();
      return;
    }
    setUnticked(false);
    setPhase('submitting');
    record(transport, source, owed)
      .then(() => {
        setPhase('accepted');
        // Re-read so what is on screen is the server's and not a guess.
        reload();
        onAccepted?.();
        goBack();
      })
      .catch((thrown: unknown) => {
        const error = toApiError(thrown);
        if (error.status === 409) {
          // `acceptTerms`: "This author has already accepted this version. Double acceptance is
          // impossible by construction" - THIS IS SUCCESS. Two tabs must not produce an error.
          setPhase('accepted');
          reload();
          onAccepted?.();
          goBack();
          return;
        }
        if (error.status === 404) {
          // "No such version, or it is superseded." Refetch and re-present; never a raw error. A
          // new version is a new thing to read, so its box starts empty again.
          setPhase('superseded');
          setTicked({});
          reload();
          return;
        }
        if (error.status === 429) {
          setThrottleWait(error instanceof ThrottledError ? error.retryAfterSeconds : undefined);
          setPhase('throttled');
          return;
        }
        setPhase('failed');
      });
  }, [transport, source, owed, ticked, reload, onAccepted, goBack]);

  const goToSignIn = () => {
    navigate(CABINET_ROUTES.signIn);
  };

  // ---------------------------------------------------------------- session gone
  if (
    agreements.status === 'failed' &&
    agreements.error !== null &&
    isUnauthenticated(agreements.error)
  ) {
    return (
      <Shell>
        <FailureBlock
          title={t('page.sessionExpiredTitle')}
          body={t('page.sessionExpiredBody')}
          onRetry={goToSignIn}
          retryLabel={t('page.signInAgain')}
        />
      </Shell>
    );
  }

  // ---------------------------------------------------------------- the read failed
  if (agreements.status === 'failed' && agreements.error !== null) {
    // Both reads failed - the list, and the author agreement's own read behind it. Nothing has
    // been recorded and nothing has changed for the author — say that, because the fear on this
    // screen is that something was half-done.
    return (
      <Shell>
        <FailureBlock
          title={t('terms.unavailableTitle')}
          body={t('terms.unavailableBody')}
          {...(isRetryable(agreements.error)
            ? {
                onRetry: agreements.reload,
                retryLabel: t('terms.retry'),
                retrying: agreements.reloading,
              }
            : {})}
        />
      </Shell>
    );
  }

  // ---------------------------------------------------------------- loading
  if (agreements.status === 'loading' || source === null) {
    return (
      <Shell>
        <LoadingBlock label={t('terms.loading')} lines={4} />
      </Shell>
    );
  }

  const accepted = owed.length === 0 || phase === 'accepted';
  const material = owed.some((document) => document.live?.isMaterialChange === true);

  return (
    <Shell>
      <Stack gap="lg">
        {blockedAction && !accepted ? (
          <Alert role="alert" variant="light" color="moon" title={t('terms.blockedNoticeTitle')}>
            <Text size="sm">{t('terms.blockedNoticeBody')}</Text>
          </Alert>
        ) : null}

        {phase === 'superseded' ? (
          <Alert role="alert" variant="light" color="gray" title={t('terms.supersededTitle')}>
            <Text size="sm">{t('terms.supersededBody')}</Text>
          </Alert>
        ) : null}

        {phase === 'throttled' ? (
          <Alert role="alert" variant="light" color="gray" title={t('terms.throttledTitle')}>
            <Text size="sm">{tooManyRequests(throttleWait, t('terms.throttledBody'))}</Text>
          </Alert>
        ) : null}

        {phase === 'failed' ? (
          <FailureBlock title={t('terms.failedTitle')} body={t('terms.failedBody')} />
        ) : null}

        <Text size="sm" c="dimmed">
          {accepted
            ? t('terms.introAlreadyAccepted')
            : material
              ? t('terms.introMaterialChange')
              : t('terms.introFirstTime')}
        </Text>

        {accepted
          ? null
          : owed.map((document) => (
              <div key={document.documentCode} className={classes.termsFacts}>
                <Stack gap="xs">
                  <Group gap="xs" align="center">
                    <Badge size="sm" variant="light" color="moon">
                      {t('page.termsActionNeededBadge')}
                    </Badge>
                    <Text size="sm" fw={700}>
                      {agreementName(document)}
                    </Text>
                  </Group>

                  {document.live === null ? null : (
                    <Text size="xs" c="dimmed">
                      <Trans
                        t={t}
                        i18nKey="page.termsVersionLine"
                        values={{
                          version: document.live.versionLabel,
                          day: formatDay(document.live.effectiveFrom),
                        }}
                        components={{ time: <time dateTime={document.live.effectiveFrom} /> }}
                      />
                    </Text>
                  )}

                  {document.live?.isMaterialChange === true ? (
                    <Text size="xs" c="dimmed">
                      {t('terms.materialChangeNote')}
                    </Text>
                  ) : null}

                  {document.live === null ? null : (
                    <a
                      href={document.live.contentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 'var(--mantine-font-size-sm)', fontWeight: 600 }}
                    >
                      {t('terms.openDocument')}
                      <OffsiteMark />
                    </a>
                  )}

                  <Checkbox
                    id={boxId(document)}
                    label={acceptWords(document)}
                    checked={ticked[boxId(document)] === true}
                    disabled={phase === 'submitting'}
                    onChange={(event) => {
                      const next = event.currentTarget.checked;
                      setTicked((current) => ({ ...current, [boxId(document)]: next }));
                    }}
                  />
                </Stack>
              </div>
            ))}

        {accepted ? null : (
          <Text size="xs" c="dimmed">
            {t('terms.documentIsOffsite')}
          </Text>
        )}

        {/* What acceptance does and does not gate. Shown whether or not anything is owed, because
            an author who has just accepted deserves to know what was ever at stake. */}
        <Text size="xs" c="dimmed">
          {t('page.termsBlocksBody')}
        </Text>

        {unticked && !accepted ? (
          <Text size="sm" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
            {t('terms.tickEveryBox')}
          </Text>
        ) : null}

        {accepted ? (
          <Stack gap="sm" align="flex-start">
            {phase === 'accepted' ? <Text size="sm">{t('terms.acceptedBody')}</Text> : null}
            <Button variant="filled" onClick={goBack}>
              {t('terms.continueButton')}
            </Button>
          </Stack>
        ) : (
          <Group gap="sm">
            <Button variant="filled" onClick={onAccept} loading={phase === 'submitting'}>
              {t('terms.acceptButton')}
            </Button>
            {onNotNow === undefined ? null : (
              <Button variant="subtle" onClick={onNotNow} disabled={phase === 'submitting'}>
                {t('terms.notNowButton')}
              </Button>
            )}
            {/* The button's VISIBLE label never changes — an accessible name that stops containing
                the visible text breaks "Label in Name" for anyone driving this by voice. The busy
                state is announced through a live region instead, which is what a live region is
                for; `loading` on its own only spins. */}
            <VisuallyHidden role="status">
              {phase === 'submitting' ? t('terms.acceptBusy') : ''}
            </VisuallyHidden>
            {/* NO "decline", NO "remind me later", NO withdrawal. There is no such transition.
                "Not now" is not one either: it records nothing - it is only the way back
                out. */}
          </Group>
        )}

        {IS_FIXTURE_TRANSPORT ? (
          <p className={classes.fixtureNotice}>
            Fixture data. This build does not call MoonX — these terms are a stand-in until
            VITE_AUTHORS_API_LIVE is set (features/support/api/transport.ts).
          </p>
        ) : null}
      </Stack>
    </Shell>
  );
}

/**
 * The page around every state. ★ Design 15 (the owner's live look at real cabinets, 2026-10-01):
 * `/terms` is drawn outside the cabinet's shell, so it brings the shell's field of stars itself, and
 * its one `<h1>` takes the prototype's title size, as every cabinet screen's does.
 */
function Shell({ children }: { readonly children: ReactNode }) {
  const { t } = useTranslation('support');
  return (
    <div className={classes.interstitialFrame}>
      <div className={classes.sky} aria-hidden="true" />
      <Container size="sm" py="xl" className={classes.interstitial}>
        <Stack gap="lg">
          <Title
            order={1}
            style={{ fontSize: 'clamp(26px, 3.4vw, 34px)', letterSpacing: '-0.02em' }}
          >
            {t('terms.pageTitle')}
          </Title>
          {children}
        </Stack>
      </Container>
    </div>
  );
}
